import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, ArrowUpDown, Users, User, Minus, Plus, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { ColorDot } from '../common/PersonColor';
import { SHIFT_TYPE_SHORT, SHIFT_TYPE_KEYS, shiftTypeLabel } from '../../constants/shiftTypes';

// Per-person monthly summary of a plan (admin only). Values come from
// GET /shifts/roster-periods/:id/user-summary — same numbers as the generation report.

export const SUMMARY_STATUS = {
  below_minimum: { label: 'Below contract minimum', className: 'badge-danger' },
  within_target: { label: 'Within target', className: 'badge-success' },
  emergency: { label: 'Emergency range', className: 'badge-warning' },
  below_target: { label: 'Below target', className: 'badge-warning' },
  above_preferred: { label: 'Above preferred', className: 'badge-warning' },
  no_values: { label: 'No contract/preference set', className: 'badge-secondary' },
};

const STATUS_TITLE = {
  below_minimum: 'Planned regular hours are below the contract minimum.',
  within_target: 'Planned regular shifts are within the target (90 % of preferred), at most 1 below it.',
  below_target: 'More than 1 regular shift below the target (90 % of preferred); the contract minimum is met.',
  emergency: 'More than the target (90 %), up to the emergency max (110 % of preferred).',
  above_preferred: 'More than the emergency max (110 % of preferred, plus any agreed extra shifts).',
  no_values: 'Neither contract hours nor preferred shifts are set for this person.',
};

const fmt1 = (x) => (Math.round((x ?? 0) * 10) / 10).toLocaleString('en-GB', { maximumFractionDigits: 1 });
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function monthLabel(month) {
  return new Date(`${month}-01T00:00:00Z`).toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// "no TL" badges for shift types the person is not qualified for
const BADGE_STYLE = {
  fontSize: '0.68rem', fontWeight: 600, padding: '0 6px', borderRadius: '999px', whiteSpace: 'nowrap',
  background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1',
};

export function NotQualifiedBadges({ types = [] }) {
  if (!types.length) return null;
  if (types.length >= SHIFT_TYPE_KEYS.length) {
    return <span style={{ ...BADGE_STYLE, background: 'var(--warning-light)', borderColor: 'var(--warning)', color: '#E65100' }} title="Not qualified for any shift type">no shift types</span>;
  }
  return (
    <span style={{ display: 'inline-flex', gap: '3px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
      {types.map((t) => (
        <span
          key={t}
          title={`Not qualified for ${shiftTypeLabel(t) || t}`}
          style={BADGE_STYLE}
        >
          no {SHIFT_TYPE_SHORT[t] || t}
        </span>
      ))}
    </span>
  );
}

function StatusBadges({ row }) {
  const s = SUMMARY_STATUS[row.status] || SUMMARY_STATUS.no_values;
  return (
    <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
      <span className={`badge ${s.className}`} title={STATUS_TITLE[row.status]} style={{ whiteSpace: 'nowrap' }}>{s.label}</span>
      {row.extra_used && (
        <span className="badge badge-info" title={`Uses extra shifts beyond the regular emergency max of ${row.base_emergency_max}.`} style={{ whiteSpace: 'nowrap' }}>
          Extra agreed
        </span>
      )}
      {row.minijob_cap_exceeded && (
        <span className="badge badge-danger" title="All planned hours (incl. events) exceed the minijob cap." style={{ whiteSpace: 'nowrap' }}>
          Above minijob cap
        </span>
      )}
    </span>
  );
}

// "max 12 (+2 extra)"
function maxText(row) {
  return `max ${row.emergency_max}${row.extra_shifts ? ` (+${row.extra_shifts} extra)` : ''}`;
}

// ── Extra allowance stepper (per user, month and plan) ───────────────────────
const MAX_EXTRA = 10;

export function ExtraStepper({ row, periodId, onChanged, compact = false }) {
  const [value, setValue] = useState(row.extra_shifts || 0);
  const [note, setNote] = useState(row.extra_note || '');
  const [noteOpen, setNoteOpen] = useState(!!row.extra_note && !compact);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(row.extra_shifts || 0); setNote(row.extra_note || ''); }, [row.extra_shifts, row.extra_note]);

  const save = async (nextValue, nextNote) => {
    setSaving(true);
    try {
      await api.put(`/shifts/roster-periods/${periodId}/extra-allowance`, {
        user_id: row.user_id, month: row.month, extra_shifts: nextValue, note: nextNote || null,
      });
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to save the extra allowance.');
      setValue(row.extra_shifts || 0);
      setNote(row.extra_note || '');
    } finally {
      setSaving(false);
    }
  };
  const step = (d) => {
    const next = Math.max(0, Math.min(MAX_EXTRA, value + d));
    if (next === value) return;
    setValue(next);
    save(next, note);
  };
  const btn = {
    width: '26px', height: '26px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: '1px solid var(--primary-dark)', background: 'var(--white)', color: 'var(--primary-dark)',
    borderRadius: '6px', cursor: 'pointer', padding: 0,
  };
  const isMinijob = row.employment_type === 'minijob';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: compact ? '120px' : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
        {!compact && <span style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>Extra shifts</span>}
        <button type="button" style={{ ...btn, opacity: value === 0 ? 0.4 : 1 }} onClick={() => step(-1)} disabled={saving || value === 0} aria-label="One extra shift less">
          <Minus size={13} />
        </button>
        <span style={{ minWidth: '22px', textAlign: 'center', fontWeight: 700, color: value ? 'var(--primary-dark)' : 'var(--text-muted)' }} aria-live="polite">
          {value ? `+${value}` : '0'}
        </span>
        <button type="button" style={{ ...btn, opacity: value === MAX_EXTRA ? 0.4 : 1 }} onClick={() => step(1)} disabled={saving || value === MAX_EXTRA} aria-label="One extra shift more">
          <Plus size={13} />
        </button>
        <button
          type="button"
          onClick={() => setNoteOpen((v) => !v)}
          style={{ background: 'none', border: 'none', padding: 0, fontSize: '0.75rem', color: 'var(--primary-dark)', cursor: 'pointer', textDecoration: 'underline' }}
          title={note || 'Add a note'}
        >
          {note ? 'Note ✎' : 'Note'}
        </button>
      </div>
      {isMinijob && value > 0 && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.72rem', color: '#E65100' }}>
          <AlertTriangle size={11} /> Minijob cap still applies
        </span>
      )}
      {noteOpen && (
        <input
          className="form-input"
          style={{ fontSize: '0.8rem', padding: '4px 8px', minWidth: '140px' }}
          value={note}
          maxLength={500}
          placeholder="e.g. agreed by phone on 12 Oct"
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => { if ((note || '') !== (row.extra_note || '')) save(value, note); }}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          disabled={saving}
          aria-label="Note for the extra allowance"
        />
      )}
    </div>
  );
}

function eventsText(row) {
  if (!row.event_slots) return null;
  return `+ ${row.events.map((e) => `${e.slots} ${e.name}`).join(', ')} (${fmt1(row.event_hours)} h)`;
}

function infoLine(row) {
  const parts = [];
  if (row.vacation_days) parts.push(`🌴 ${plural(row.vacation_days, 'vacation day')}`);
  if (row.absence_days) parts.push(`${plural(row.absence_days, 'absence day')}`);
  if (row.blocked_days) parts.push(`${plural(row.blocked_days, 'blocked day')}`);
  return parts.length ? parts.join(' · ') : 'No vacation, absences or blocked days';
}

// ── Selected person: one block per calendar month of the plan ────────────────
export function PersonSummaryCard({ summary, userId, loading, periodId, onChanged }) {
  const rows = (summary?.rows || []).filter((r) => r.user_id === userId);
  const person = summary?.users?.find((u) => u.id === userId);

  return (
    <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        {person ? <ColorDot userId={person.id} size={12} /> : <User size={16} style={{ color: 'var(--primary-dark)' }} />}
        <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0 }}>
          Monthly summary{person ? ` — ${person.name}` : ''}
        </h3>
        {person?.employment_type === 'minijob' && <span className="badge badge-info">Minijob</span>}
        <NotQualifiedBadges types={person?.not_qualified} />
        {loading && <div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />}
      </div>

      {!summary && !loading && (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Summary not available.</p>
      )}
      {summary && rows.length === 0 && (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>No data for this person in this plan.</p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '10px' }}>
        {rows.map((r) => {
          const ev = eventsText(r);
          return (
            <div
              key={r.month}
              style={{
                border: '1px solid var(--primary-light)', borderRadius: 'var(--radius)',
                background: 'var(--primary-xlight)', padding: '10px 12px', fontSize: '0.85rem',
                display: 'flex', flexDirection: 'column', gap: '4px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '2px' }}>
                <strong style={{ fontSize: '0.92rem' }}>{monthLabel(r.month)}</strong>
                {r.partial && (
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-light)', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '999px', padding: '0 7px' }}>
                    partial: {r.days_in_plan}/{r.days_in_month} days
                  </span>
                )}
                <span style={{ marginLeft: 'auto' }}><StatusBadges row={r} /></span>
              </div>
              <div>
                <strong>{plural(r.planned_shifts, 'shift')} planned</strong>
                {!r.no_values && <> · preferred {fmt1(r.preferred_month)}</>}
                {(!r.no_values || r.extra_shifts > 0) && <> · {maxText(r)}</>}
              </div>
              {!r.no_values && (
                <div style={{ color: 'var(--text-light)' }}>
                  Target {r.target} (90 %) · emergency max {r.base_emergency_max} (110 %)
                </div>
              )}
              <div>
                {fmt1(r.planned_hours)} h regular
                {' · '}
                {r.min_hours > 0 ? <>contract minimum {fmt1(r.min_hours)} h</> : <span style={{ color: 'var(--text-muted)' }}>no contract minimum</span>}
              </div>
              {ev && <div style={{ color: '#5B21B6' }}>{ev}</div>}
              <div>
                Total planned: <strong>{fmt1(r.total_hours)} h</strong>
                {r.employment_type === 'minijob' && r.cap_hours != null && (
                  <span style={{ color: r.minijob_cap_exceeded ? 'var(--danger)' : 'var(--text-light)' }}>
                    {' '}· minijob cap {fmt1(r.cap_hours)} h
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', borderTop: '1px solid var(--primary-light)', paddingTop: '4px', marginTop: '2px' }}>
                {infoLine(r)}
              </div>
              {periodId && <ExtraStepper row={r} periodId={periodId} onChanged={onChanged} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── All staff: collapsible team table, one row per person and month ──────────
const TEAM_OPEN_KEY = 'leuchtstoff_teamOverviewOpen';

export function TeamOverviewCard({ summary, loading, onSelectUser, periodId, onChanged }) {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(TEAM_OPEN_KEY) === '1'; } catch { return false; }
  });
  const toggle = () => {
    setOpen((v) => {
      try { localStorage.setItem(TEAM_OPEN_KEY, v ? '0' : '1'); } catch { /* ignore */ }
      return !v;
    });
  };
  const [sort, setSort] = useState({ key: 'name', dir: 1 });
  const setSortKey = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: key === 'deviation' ? -1 : 1 }));

  const rows = useMemo(() => {
    const list = [...(summary?.rows || [])];
    const byName = (a, b) => a.name.localeCompare(b.name) || a.user_id - b.user_id || a.month.localeCompare(b.month);
    if (sort.key === 'deviation') {
      // People without values have no preference to deviate from → always last
      list.sort((a, b) => (a.no_values - b.no_values) || sort.dir * (a.deviation - b.deviation) || byName(a, b));
    } else {
      list.sort((a, b) => sort.dir * byName(a, b));
    }
    return list;
  }, [summary, sort]);

  const thStyle = { padding: '8px 10px', fontSize: '0.75rem' };
  const tdStyle = { padding: '8px 10px', fontSize: '0.85rem', whiteSpace: 'nowrap' };
  const sortBtn = (key, label) => (
    <button
      type="button"
      onClick={() => setSortKey(key)}
      title={`Sort by ${label.toLowerCase()}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'none', border: 'none', padding: 0,
        font: 'inherit', color: sort.key === key ? 'var(--primary-dark)' : 'inherit', cursor: 'pointer',
        textTransform: 'inherit', letterSpacing: 'inherit', fontWeight: 'inherit',
      }}
    >
      {label} <ArrowUpDown size={11} />
    </button>
  );

  return (
    <div className="card" style={{ marginBottom: '16px', padding: 0, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px',
          background: 'var(--primary-xlight)', border: 'none', cursor: 'pointer', textAlign: 'left',
          fontSize: '0.95rem', fontWeight: 700, color: 'var(--text)',
        }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Users size={16} style={{ color: 'var(--primary-dark)' }} />
        Team overview
        {summary && (
          <span style={{ fontWeight: 400, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
            {plural(summary.users.length, 'person', 'people')}
          </span>
        )}
        {loading && <div className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />}
      </button>

      {open && (
        <div style={{ padding: '12px 16px' }}>
          {!summary && !loading && <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Summary not available.</p>}
          {summary && (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th style={thStyle}>{sortBtn('name', 'Name')}</th>
                    <th style={thStyle}>Month</th>
                    <th style={thStyle}>{sortBtn('deviation', 'Shifts / preferred')}</th>
                    <th style={thStyle}>Hours / minimum</th>
                    <th style={thStyle}>Events</th>
                    <th style={thStyle}>Status</th>
                    {periodId && <th style={thStyle}>Extra</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.user_id}-${r.month}`}>
                      <td style={tdStyle}>
                        <button
                          type="button"
                          onClick={() => onSelectUser(r.user_id)}
                          title={`Show plan for ${r.name}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text)', fontWeight: 600, font: 'inherit' }}
                        >
                          <ColorDot userId={r.user_id} size={10} />
                          <span style={{ textDecoration: 'underline', textDecorationColor: 'var(--primary-dark)' }}>{r.name}</span>
                        </button>
                        {r.not_qualified?.length > 0 && (
                          <span style={{ marginLeft: '6px' }}><NotQualifiedBadges types={r.not_qualified} /></span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {monthLabel(r.month)}
                        {r.partial && (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}> (partial: {r.days_in_plan}/{r.days_in_month} days)</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {r.planned_shifts}
                        {!r.no_values && (
                          <span style={{ color: 'var(--text-light)' }}>
                            {' / '}{fmt1(r.preferred_month)}
                            <span style={{ color: r.deviation > 0 ? '#E65100' : r.deviation < 0 ? 'var(--primary-dark)' : 'var(--text-muted)', fontSize: '0.75rem' }}>
                              {' '}({r.deviation > 0 ? '+' : ''}{fmt1(r.deviation)})
                            </span>
                          </span>
                        )}
                        {(!r.no_values || r.extra_shifts > 0) && (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}> · {maxText(r)}</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {fmt1(r.planned_hours)} h
                        <span style={{ color: 'var(--text-light)' }}> / {fmt1(r.min_hours)} h</span>
                        {r.employment_type === 'minijob' && r.cap_hours != null && (
                          <span style={{ color: r.minijob_cap_exceeded ? 'var(--danger)' : 'var(--text-muted)', fontSize: '0.75rem' }}>
                            {' '}· {fmt1(r.total_hours)} / {fmt1(r.cap_hours)} h cap
                          </span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {r.event_slots
                          ? <span title={eventsText(r)}>{r.events.map((e) => `${e.slots} ${e.name}`).join(', ')} ({fmt1(r.event_hours)} h)</span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={tdStyle}><StatusBadges row={r} /></td>
                      {periodId && (
                        <td style={{ ...tdStyle, whiteSpace: 'normal' }}>
                          <ExtraStepper row={r} periodId={periodId} onChanged={onChanged} compact />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
